import { cn } from '../../lib/cn';

/**
 * Iconos e ilustraciones de la interfaz.
 *
 * Existen porque el panel usaba emoji —💾 🗂 🔗 🖼— y un emoji no es un icono:
 * lo dibuja el sistema operativo, así que cambia de forma y de color entre
 * Windows, Android y macOS, no hereda el color de marca y en una captura de
 * pantalla de un cliente aparece distinto al de la documentación.
 *
 * Todo se dibuja con `currentColor` y trazo de 1,6, así que un icono se
 * recolorea cambiando el color del texto que lo contiene.
 */

const TRAZOS = {
  /** Almacenamiento: la nube de la marca, reducida a línea. */
  storage: (
    <>
      <path d="M7.5 18h9a4 4 0 0 0 .5-8 5.5 5.5 0 0 0-10.6 1A3.4 3.4 0 0 0 7.5 18Z" />
    </>
  ),
  files: (
    <>
      <path d="M3.5 7.5a2 2 0 0 1 2-2h3.6l2 2.5h7.4a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-13a2 2 0 0 1-2-2Z" />
    </>
  ),
  link: (
    <>
      <path d="m9.6 14.4 4.8-4.8" />
      <path d="m10.8 7.6 1.8-1.8a3.8 3.8 0 0 1 5.4 5.4l-1.8 1.8" />
      <path d="m13.2 16.4-1.8 1.8a3.8 3.8 0 0 1-5.4-5.4l1.8-1.8" />
    </>
  ),
  photo: (
    <>
      <rect x="3.5" y="5.5" width="17" height="13" rx="2.5" />
      <circle cx="9" cy="10.2" r="1.4" />
      <path d="m4.6 17.2 4.4-4.4 3 3 3.4-3.4 4.1 4.4" />
    </>
  ),
  upload: (
    <>
      <path d="M12 16V4.5" />
      <path d="m7.8 8.7 4.2-4.2 4.2 4.2" />
      <path d="M4.5 15v3.5a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V15" />
    </>
  ),
  /** Cifrado y protección. */
  shield: (
    <>
      <path d="M12 3 4.5 6v5.4c0 4.3 3 8.3 7.5 9.6 4.5-1.3 7.5-5.3 7.5-9.6V6L12 3Z" />
      <path d="m9.2 12.1 2 2 3.6-3.9" />
    </>
  ),
  /** Segundo factor: el teléfono con su código. */
  device: (
    <>
      <rect x="6" y="3" width="12" height="18" rx="2.5" />
      <path d="M10 7h4M10 10.5h4M10 14h4" />
      <circle cx="12" cy="17.6" r="0.8" fill="currentColor" stroke="none" />
    </>
  ),
  /** Lo que no se guarda. */
  eyeOff: (
    <>
      <path d="M3.5 12S6.8 6.5 12 6.5c1.3 0 2.5.35 3.5.9M20.5 12s-1.1 1.85-3 3.5" />
      <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
      <path d="m4.5 4.5 15 15" />
    </>
  ),
  /** Llevarse los datos. */
  download: (
    <>
      <path d="M12 3.5v11" />
      <path d="m8 10.8 4 3.9 4-3.9" />
      <path d="M4.5 16v2.5a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V16" />
    </>
  ),
  /** Idioma. */
  globe: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M3.5 12h17" />
      <path d="M12 3.5c2.2 2.3 3.4 5.3 3.4 8.5s-1.2 6.2-3.4 8.5c-2.2-2.3-3.4-5.3-3.4-8.5S9.8 5.8 12 3.5Z" />
    </>
  ),
  star: (
    <>
      <path d="m12 4.5 2.4 4.9 5.4.8-3.9 3.8.9 5.4-4.8-2.5-4.8 2.5.9-5.4L4.2 10.2l5.4-.8Z" />
    </>
  ),
} as const;

export type IconName = keyof typeof TRAZOS;

export function Icon({
  name,
  className,
  ...rest
}: { name: IconName; className?: string } & React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={cn('h-5 w-5', className)}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...rest}
    >
      {TRAZOS[name]}
    </svg>
  );
}

/**
 * Ilustraciones de las tres cosas que se hacen con un archivo.
 *
 * Están dibujadas con **la misma regla que el logotipo**: las piezas crecen de
 * izquierda a derecha o de arriba abajo, y el ámbar se aclara según crecen. No
 * es decoración: es lo que hace que el producto y la marca se reconozcan como
 * la misma cosa, en lugar de parecer un logo bonito encima de una aplicación
 * cualquiera.
 *
 * Van sin texto dentro a propósito —el texto lo pone quien las usa, desde el
 * catálogo de idiomas—, y `aria-hidden` porque siempre acompañan a un título
 * que ya dice lo mismo.
 */
const ILUSTRACIONES = {
  /** Subir: el archivo asciende hacia la nube de la marca. */
  upload: (
    <>
      <g fill="rgb(var(--c-brand-5))">
        <rect x="18" y="24" width="60" height="14" rx="7" />
        <circle cx="28" cy="24" r="9" />
        <circle cx="46" cy="20" r="14" />
        <circle cx="63" cy="17" r="15" />
      </g>
      <g stroke="rgb(var(--c-brand-4))" strokeWidth="5" strokeLinecap="round" fill="none">
        <path d="M48 66V44" />
        <path d="m40 51 8-8 8 8" strokeLinejoin="round" />
      </g>
    </>
  ),

  /** Organizar: tres bandejas que crecen hacia el frente. */
  organize: (
    <>
      <rect x="27" y="14" width="42" height="11" rx="4" fill="rgb(var(--c-brand-2))" />
      <rect x="21" y="31" width="54" height="13" rx="5" fill="rgb(var(--c-brand-3))" />
      <rect x="14" y="50" width="68" height="15" rx="6" fill="rgb(var(--c-brand-5))" />
    </>
  ),

  /** Compartir: uno decide, dos reciben. */
  share: (
    <>
      <g stroke="rgb(var(--c-brand-3))" strokeWidth="4" strokeLinecap="round">
        <path d="m34 36 26-14" />
        <path d="m34 40 26 14" />
      </g>
      <circle cx="24" cy="38" r="12" fill="rgb(var(--c-brand-5))" />
      <circle cx="70" cy="20" r="9" fill="rgb(var(--c-brand-3))" />
      <circle cx="70" cy="56" r="9" fill="rgb(var(--c-brand-4))" />
    </>
  ),

  /** Nada guardado todavía: la nube vacía, con su hueco. */
  empty: (
    <>
      <g fill="rgb(var(--c-surface-2))">
        <rect x="18" y="34" width="60" height="14" rx="7" />
        <circle cx="28" cy="34" r="9" />
        <circle cx="46" cy="30" r="14" />
        <circle cx="63" cy="27" r="15" />
      </g>
      <g stroke="rgb(var(--c-line-strong))" strokeWidth="3.5" strokeLinecap="round">
        <path d="M40 60h16" />
      </g>
    </>
  ),
} as const;

export type SpotName = keyof typeof ILUSTRACIONES;

export function Spot({ name, className }: { name: SpotName; className?: string }) {
  return (
    <svg viewBox="0 0 96 72" className={cn('h-16 w-[5.5rem]', className)} aria-hidden="true">
      {ILUSTRACIONES[name]}
    </svg>
  );
}
